import type { KeyboardEvent, MouseEvent, SyntheticEvent } from "react";

const SCROLL_OFFSET_PX = 12;
const ACCORDION_TRIGGER_SELECTOR = "button[data-testid$='-filter-trigger']";

/**
 * Walks up from `start` (exclusive) looking for the nearest ancestor whose
 * computed overflow-y allows scrolling. Stops at `document.body` to avoid
 * scrolling the entire page. Returns `boundary` as a fallback so desktop
 * (where the aside itself scrolls via Tailwind responsive class) still works.
 */
export function findScrollableAncestor(
	start: HTMLElement,
	boundary: HTMLElement,
): HTMLElement {
	let current = start.parentElement;
	while (current && current !== document.body) {
		const { overflowY } = globalThis.getComputedStyle(current);
		if (overflowY === "auto" || overflowY === "scroll") {
			return current;
		}
		current = current.parentElement;
	}
	return boundary;
}

function scrollTriggerIntoView(
	trigger: HTMLElement,
	container: HTMLElement,
): void {
	globalThis.requestAnimationFrame(() => {
		if (trigger.getAttribute("aria-expanded") !== "true") {
			return;
		}
		const containerRect = container.getBoundingClientRect();
		const triggerRect = trigger.getBoundingClientRect();
		const targetTop =
			container.scrollTop +
			(triggerRect.top - containerRect.top) -
			SCROLL_OFFSET_PX;
		container.scrollTo({
			top: Math.max(targetTop, 0),
			behavior: "smooth",
		});
	});
}

function findTrigger(event: SyntheticEvent<HTMLElement>): HTMLElement | null {
	return (event.target as HTMLElement).closest<HTMLElement>(
		ACCORDION_TRIGGER_SELECTOR,
	);
}

/**
 * Scrolls the expanded accordion trigger into view within the nearest
 * scrollable ancestor. Uses event delegation so a single handler on the
 * container covers all current and future filter triggers. Works on both
 * the desktop sidebar (aside scrolls) and the mobile drawer (parent div
 * scrolls).
 */
export function handleFilterTriggerClick(event: MouseEvent<HTMLElement>): void {
	const trigger = findTrigger(event);
	if (trigger) {
		const scrollable = findScrollableAncestor(trigger, event.currentTarget);
		scrollTriggerIntoView(trigger, scrollable);
	}
}

/** Keyboard counterpart for accordion triggers activated via Enter or Space. */
export function handleFilterTriggerKeyDown(
	event: KeyboardEvent<HTMLElement>,
): void {
	if (event.key !== "Enter" && event.key !== " ") {
		return;
	}
	const trigger = findTrigger(event);
	if (trigger) {
		const scrollable = findScrollableAncestor(trigger, event.currentTarget);
		scrollTriggerIntoView(trigger, scrollable);
	}
}
